import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import { useResizeObserver } from '#src/hooks/ui/useResizeObserver.ts';
import { capitalize } from '#src/lib/humanize.ts';

const Wrapper = styled.div`
  position: absolute;

  &.anchorTop.alignStart,
  &.anchorBottom.alignStart {
    left: var(--left);
    padding-right: var(--double-unit);
  }

  &.alignCenter {
    left: calc(
      var(--left) + (var(--right) - var(--left) - var(--componentWidth)) / 2
    );
  }

  &.anchorTop {
    top: calc(var(--top) - var(--componentHeight) - var(--double-unit));
  }

  &.anchorBottom {
    top: calc(var(--bottom) + var(--double-unit));
  }

  &.fullWidth {
    left: var(--unit);
    right: var(--unit);
  }
`;

export type Props = {
  children: ReactNode;
  style?: CSSProperties;
  positionProps?: Partial<{
    top: number;
    left: number;
    right: number;
    bottom: number;
    ready: boolean;
  }>;
  className?: string;
  fullWidth?: boolean;
  anchor?: 'top' | 'bottom';
  align?: 'start' | 'center' | 'end';
};

export const HighlightExtraElementWrapper = ({
  anchor,
  align,
  style,
  fullWidth,
  children,
  className,
  positionProps = {},
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const {
    top = 0,
    left = 0,
    right = 0,
    bottom = 0,
    ready = true,
  } = positionProps;

  useResizeObserver(ref, () => {
    const newWidth = ref.current?.offsetWidth;
    if (newWidth) {
      setDimensions({
        width: newWidth,
        height: ref.current?.offsetHeight ?? 0,
      });
    }
  });

  if (!ready) {
    return null;
  }

  return (
    <Wrapper
      style={
        {
          ...style,
          '--top': `${top}px`,
          '--left': `${left}px`,
          '--right': `${right}px`,
          '--bottom': `${bottom}px`,
          '--componentWidth': `${dimensions.width}px`,
          '--componentHeight': `${dimensions.height}px`,
        } as CSSProperties
      }
      ref={ref}
      className={classNames(
        className,
        fullWidth && 'fullWidth',
        anchor && `anchor${capitalize(anchor)}`,
        align && `align${capitalize(align)}`
      )}
    >
      {children}
    </Wrapper>
  );
};
